import React from 'react'
import { Button, Stack, Tooltip, Typography } from '@mui/material'
import type { FilterCardStatus } from './types'

type Props = {
    count: number
    status: FilterCardStatus
    /** Disables actions while a fresh run is in flight (avoids race with new filters). */
    runInFlight: boolean
    undoDisabled?: boolean
    undoDisabledReason?: string
    onUndo: () => void
}

export default function FilterCard({
    count,
    status,
    runInFlight,
    undoDisabled = false,
    undoDisabledReason,
    onUndo,
}: Props) {
    if (status === 'undone' || status === 'dismissed' || count <= 0) return null

    const appliedLabel = `${count} filter${count === 1 ? '' : 's'} applied to screener`
    const showUndo = !undoDisabled

    if (status === 'applied') {
        return (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.tertiary">
                    {appliedLabel}
                </Typography>
                {showUndo && (
                    <>
                        <Typography variant="caption" color="text.disabled" aria-hidden>
                            ·
                        </Typography>
                        <Tooltip title={runInFlight ? 'Wait for the current run to finish' : 'Undo this turn'}>
                            <span>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={onUndo}
                                    disabled={runInFlight}
                                    sx={{
                                        minWidth: 0,
                                        p: 0,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        lineHeight: 1.4,
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
                                    }}
                                >
                                    Undo
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
                {!showUndo && undoDisabledReason && (
                    <Typography variant="caption" color="text.disabled">
                        · {undoDisabledReason}
                    </Typography>
                )}
            </Stack>
        )
    }

    return (
        <Typography variant="caption" color="text.tertiary" display="block" sx={{ mt: 1.25 }}>
            Applying {count} filter{count === 1 ? '' : 's'}…
        </Typography>
    )
}
